/*
 * Formatting for both locales: Latin digits, Gregorian calendar, SAR amounts. Pure functions take the
 * locale explicitly; components usually call `useFormat()` (src/i18n/useFormat.ts), which binds the current
 * locale. Money renders through `<Sar amount={…} />` (re-exported here) so the riyal sign is always drawn by
 * the Saudi Riyal font.
 */
import { intlLocale, type Locale } from '@/i18n/locale'

export { Sar, RIYAL } from './Sar'
export { formatSarAmount } from './money'
import { formatSarAmount } from './money'

const nf = new Map<string, Intl.NumberFormat>()
function numberFormat(locale: Locale, opts: Intl.NumberFormatOptions = {}): Intl.NumberFormat {
  const key = locale + JSON.stringify(opts)
  let f = nf.get(key)
  if (!f) {
    f = new Intl.NumberFormat(intlLocale(locale), opts)
    nf.set(key, f)
  }
  return f
}

/** "1,200", "0.5". Latin digits with thousands separators. */
export function formatNumber(n: number, locale: Locale, opts?: Intl.NumberFormatOptions): string {
  return numberFormat(locale, opts).format(n)
}

/** Plain-text money for places that cannot render a component (aria labels, exports): "29 SAR" / "29 ريال". */
export function formatSarText(amount: number, locale: Locale): string {
  return locale === 'ar' ? `${formatSarAmount(amount)} ريال` : `SAR ${formatSarAmount(amount)}`
}

export function formatPercent(fraction: number, locale: Locale, digits = 0): string {
  return numberFormat(locale, { style: 'percent', maximumFractionDigits: digits }).format(fraction)
}

type DateInput = string | number | Date
const toDate = (d: DateInput) => (d instanceof Date ? d : new Date(d))

export type DateStyle = 'date' | 'short' | 'long' | 'datetime' | 'time' | 'month'

const DATE_OPTS: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  /** 25 Oct 2026 / 25 أكتوبر 2026 */
  date: { day: 'numeric', month: 'short', year: 'numeric' },
  /** 25 Oct / 25 أكتوبر */
  short: { day: 'numeric', month: 'short' },
  /** Sunday, 25 October 2026 */
  long: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
  /** 25 Oct 2026, 14:05 */
  datetime: { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' },
  /** 14:05 */
  time: { hour: 'numeric', minute: '2-digit' },
  /** October 2026 */
  month: { month: 'long', year: 'numeric' },
}

export function formatDate(d: DateInput, locale: Locale, style: DateStyle = 'date'): string {
  return new Intl.DateTimeFormat(intlLocale(locale), DATE_OPTS[style]).format(toDate(d))
}

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['week', 7 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
  ['second', 1],
]

/** "3 days ago", "in 5 days", "now" / "قبل 3 أيام", "بعد 5 أيام", "الآن". */
export function formatRelative(d: DateInput, locale: Locale, now: number = Date.now()): string {
  const diff = (toDate(d).getTime() - now) / 1000
  const abs = Math.abs(diff)
  const rtf = new Intl.RelativeTimeFormat(intlLocale(locale), { numeric: 'auto' })
  if (abs < 45) return rtf.format(0, 'second')
  for (const [unit, secs] of UNITS) {
    if (abs >= secs || unit === 'second') return rtf.format(Math.round(diff / secs), unit)
  }
  return rtf.format(0, 'second')
}

/** Whole days from now until a date (negative when past), rounded up: 4.2 days left reads "5 days". */
export function daysUntil(d: DateInput, now: number = Date.now()): number {
  return Math.ceil((toDate(d).getTime() - now) / 86_400_000)
}

const BYTE_UNITS = ['byte', 'kilobyte', 'megabyte', 'gigabyte', 'terabyte'] as const

/** "1.5 GB", "512 MB". Base 1024, one decimal above KB. */
export function formatBytes(bytes: number, locale: Locale): string {
  let value = bytes
  let i = 0
  while (value >= 1024 && i < BYTE_UNITS.length - 1) {
    value /= 1024
    i++
  }
  return numberFormat(locale, {
    style: 'unit',
    unit: BYTE_UNITS[i],
    unitDisplay: 'short',
    maximumFractionDigits: i === 0 ? 0 : 1,
  }).format(value)
}
